import React, { useCallback, useEffect, useState } from 'react'
import authAxios from '../../../../config/authAxios'
import SpinnerLoading from '../../../components/Spinners/SpinnerLoading'

const money = value => value === null || value === undefined ? '—' : `S/ ${Number(value).toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})}`
const number = value => value === null || value === undefined ? '—' : Number(value).toLocaleString('es-PE')
const label = value => String(value || '').replace(/_/g,' ')

/**
 * Historia 5: comparativa Nivel 1 (marketing_campana_snapshots) vs Nivel 2
 * (auditoria independiente) y sus banderas. Compartido entre la pagina nueva
 * de Operaciones (SupervisionMarketing.js, puedeAcusar=true) y la pestaña
 * "Supervisión · Nivel 2" dentro del panel de Marketing (AgenteMarketing.js,
 * puedeAcusar=false — Marketing solo lee, no puede acusar recibo de banderas).
 */
const SupervisorPanel = ({ campaignId, puedeAcusar }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [ackingId, setAckingId] = useState(null)

  const load = useCallback(() => {
    if (!campaignId) return
    setLoading(true); setError('')
    authAxios.get(`/admin/agente-marketing/supervisor?id_camp=${campaignId}`)
      .then(r => setData(r.data?.data || null))
      .catch(e => setError(e.response?.data?.msg || 'No se pudo cargar la supervisión del Nivel 2.'))
      .finally(() => setLoading(false))
  }, [campaignId])

  useEffect(() => { load() }, [load])

  const acknowledge = async idBandera => {
    setAckingId(idBandera)
    try {
      await authAxios.post(`/admin/agente-marketing/supervisor/banderas/${idBandera}/leer`)
      setNotice('Acuse registrado.')
      load()
    } catch (e) { setError(e.response?.data?.msg || 'No se pudo registrar el acuse.') }
    finally { setAckingId(null) }
  }

  if (loading) return <SpinnerLoading />
  if (error) return <div className="alert alert-danger">{error}</div>
  if (!data) return <p className="am-empty-tab">Este ciclo todavía no tiene ninguna auditoría del Nivel 2. Se genera con el cron diario de supervisión.</p>

  const { auditoria, umbral_oficial: umbral, snapshot_nivel1: snapshot, banderas } = data

  if (auditoria?.estado === 'sin_umbral_oficial') {
    return <div className="am-section">
      <p className="am-empty-tab mb-0">Este ciclo no tiene un umbral oficial congelado todavía (Operaciones no aprobó ninguna regla). El Nivel 2 no puede auditarlo hasta entonces.</p>
    </div>
  }

  const rows = [
    ['Gasto', snapshot?.gasto_acumulado, auditoria?.gasto_nivel2, money],
    ['Leads', snapshot?.leads_acumulados, auditoria?.leads_nivel2, number],
    ['Tratos', snapshot?.tratos_acumulados, auditoria?.tratos_nivel2, number],
    ['Matrículas', snapshot?.matriculas_acumuladas, auditoria?.matriculas_nivel2, number],
    ['CPL', snapshot?.cpl, auditoria?.cpl_nivel2, money],
    ['CPA', snapshot?.cpa, auditoria?.cpa_nivel2, money],
    ['CAC %', snapshot?.cac_pct, auditoria?.cac_pct_nivel2, v => v == null ? '—' : `${Number(v).toFixed(2)}%`],
    ['Semáforo volumen', snapshot?.semaforo_volumen, auditoria?.semaforo_volumen_nivel2, label],
    ['Semáforo eficiencia', snapshot?.semaforo_eficiencia, auditoria?.semaforo_eficiencia_nivel2, label],
    ['Semáforo general', snapshot?.semaforo_general, auditoria?.semaforo_general_nivel2, label],
  ]

  return <div>
    {notice && <div className="alert alert-success">{notice}<button className="close" onClick={() => setNotice('')}>&times;</button></div>}
    <div className="am-detail mb-3">
      <strong>Auditoría del {auditoria?.fecha_datos}</strong>
      <div className="text-muted">Ejecutada {auditoria?.ejecutado_en} · estado {label(auditoria?.estado)}</div>
      {umbral && <div className="text-muted">Umbral oficial usado: versión {umbral.version} · congelado el {umbral.congelado_en}</div>}
    </div>

    <div className="am-table-wrap mb-4">
      <table className="table am-table">
        <thead><tr><th>Métrica</th><th>Nivel 1</th><th>Nivel 2</th><th>Diferencia</th></tr></thead>
        <tbody>{rows.map(([nameLabel, v1, v2, fmt]) => {
          const numeric = typeof v1 === 'number' || (v1 != null && !isNaN(v1))
          const diff = numeric && v1 != null && v2 != null && Number(v1) !== 0
            ? (((Number(v2) - Number(v1)) / Math.abs(Number(v1))) * 100).toFixed(2) + '%'
            : (v1 !== v2 ? 'distinto' : '—')
          const off = v1 != null && v2 != null && String(v1) !== String(v2)
          return <tr key={nameLabel} className={off ? 'am-row-pausar' : ''}>
            <td>{nameLabel}</td><td>{fmt(v1)}</td><td>{fmt(v2)}</td><td>{diff}</td>
          </tr>
        })}</tbody>
      </table>
    </div>

    <h6>Banderas ({(banderas || []).length})</h6>
    {(!banderas || banderas.length === 0)
      ? <p className="text-muted">Sin banderas para esta auditoría.</p>
      : <div className="am-alert-list">{banderas.map(b => <div className={`am-alert am-alert-${b.severidad === 'critica' ? 'critica' : (b.severidad === 'alta' ? 'alta' : 'informativa')}`} key={b.id_bandera}>
          <span className="am-alert-icon" aria-hidden="true">{b.severidad === 'critica' ? '‼' : '!'}</span>
          <div className="am-alert-body">
            <strong>{b.titulo}</strong> <span className={`am-badge am-badge-${b.tipo === 'discrepancia' ? 'rojo' : 'amarillo'}`}>{label(b.tipo)}</span>
            <p>{b.mensaje}</p>
            {b.diferencia_pct != null && <small className="d-block text-muted">Nivel 1: {b.valor_nivel1} · Nivel 2: {b.valor_nivel2} · diferencia {Number(b.diferencia_pct).toFixed(2)}%</small>}
            <div className="am-alert-time">{b.creada_en}</div>
          </div>
          {puedeAcusar && <button className="btn btn-sm btn-outline-secondary" disabled={ackingId === b.id_bandera} onClick={() => acknowledge(b.id_bandera)}>
            {ackingId === b.id_bandera ? 'Guardando…' : 'Acusar recibo'}
          </button>}
        </div>)}</div>}
  </div>
}

export default SupervisorPanel
